import React, { useState, useRef, useEffect, useCallback } from 'react';
import JakeMessage from '../../components/JakeMessage';
import api from '../../services/api';
import './JakeOrderModal.css';

const SIZES = ['YS', 'YM', 'YL', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL'];
const BASE_SHIRT_PRICE = 8.99;
const NAME_PRINT_FEE = 10.0;
const PRICE_PER_SHIRT = BASE_SHIRT_PRICE + NAME_PRINT_FEE;

const emptyItem = () => ({ name_on_back: '', size: '', quantity: 1 });

const emptyFormData = () => ({
  customer_name: '',
  customer_phone: '',
  salesperson_id: '',
  notes: '',
  items: [emptyItem()],
});

const JakeOrderModal = ({ isOpen, onClose, onOrdersCreated }) => {
  const [mode, setMode] = useState('ai'); // 'ai' or 'manual'

  // AI mode state
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [ordersCreated, setOrdersCreated] = useState(false);

  // Manual mode state
  const [formData, setFormData] = useState(emptyFormData());
  const [users, setUsers] = useState([]);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState(null);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (isOpen && mode === 'ai' && !isStreaming) {
      inputRef.current?.focus();
    }
  }, [isOpen, isStreaming, mode]);

  useEffect(() => {
    if (isOpen) {
      setMode('ai');
      setMessages([]);
      setInput('');
      setOrdersCreated(false);
      setIsStreaming(false);
      setFormData(emptyFormData());
      setFormError(null);
      fetchUsers();
    }
  }, [isOpen]);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/spikes-gear/users/active');
      setUsers(response.data.users || []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const buildHistory = useCallback(() => {
    return messages.map(m => ({
      role: m.role,
      content: m.content
    }));
  }, [messages]);

  const sendMessage = useCallback(async () => {
    if (!input.trim() || isStreaming) return;

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: input.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const history = buildHistory();
    setInput('');
    setIsStreaming(true);

    const assistantId = Date.now() + 1;
    setMessages(prev => [...prev, {
      id: assistantId,
      role: 'assistant',
      content: '',
      timestamp: new Date()
    }]);

    try {
      const response = await fetch('/api/jake/spikes-gear-chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          message: userMessage.content,
          history: history
        })
      });

      if (!response.ok) {
        throw new Error(`Request failed: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullContent = '';
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));

              if (data.type === 'text') {
                fullContent += data.content;
                setMessages(prev => prev.map(m =>
                  m.id === assistantId ? { ...m, content: fullContent } : m
                ));
              } else if (data.type === 'orders_created') {
                setOrdersCreated(true);
                if (onOrdersCreated) {
                  onOrdersCreated(data.orders);
                }
              } else if (data.type === 'done') {
                if (ordersCreated) {
                  setTimeout(() => onClose(), 2000);
                }
              }
            } catch (e) {}
          }
        }
      }
    } catch (error) {
      setMessages(prev => prev.map(m =>
        m.id === assistantId
          ? { ...m, content: 'Sorry, something went wrong. Please try again.' }
          : m
      ));
    } finally {
      setIsStreaming(false);
    }
  }, [input, isStreaming, buildHistory, ordersCreated, onClose, onOrdersCreated]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleClose = () => {
    if (!isStreaming && !formLoading) {
      onClose();
    }
  };

  // Manual form handlers
  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const handleFormChange = (field, value) => {
    if (field === 'customer_phone') {
      setFormData(prev => ({ ...prev, [field]: formatPhoneNumber(value) }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleItemChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
    }));
  };

  const addItem = () => {
    setFormData(prev => ({ ...prev, items: [...prev.items, emptyItem()] }));
  };

  const removeItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.length === 1 ? prev.items : prev.items.filter((_, i) => i !== index),
    }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError(null);

    try {
      const payload = {
        customer_name: formData.customer_name,
        customer_phone: formData.customer_phone,
        items: formData.items.map(i => ({
          name_on_back: i.name_on_back.trim(),
          size: i.size,
          quantity: parseInt(i.quantity),
        })),
        salesperson_id: formData.salesperson_id || null,
        notes: formData.notes,
      };

      const response = await api.post('/spikes-gear/orders', payload);

      if (response.data.success) {
        if (onOrdersCreated) {
          onOrdersCreated([{ id: response.data.orderId }]);
        }
        onClose();
      }
    } catch (err) {
      setFormError(err.response?.data?.message || 'Failed to create order');
    } finally {
      setFormLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="jake-order-modal-overlay" onClick={handleClose}>
      <div className={`jake-order-modal ${mode === 'manual' ? 'jake-order-modal-wide' : ''}`} onClick={e => e.stopPropagation()}>
        {/* Header with Tabs */}
        <div className="jake-order-modal-header">
          <div className="jake-order-modal-tabs">
            <button
              className={`jake-order-tab ${mode === 'ai' ? 'active' : ''}`}
              onClick={() => setMode('ai')}
            >
              <span className="jake-order-tab-icon">J</span>
              Ask Jake
            </button>
            <button
              className={`jake-order-tab ${mode === 'manual' ? 'active' : ''}`}
              onClick={() => setMode('manual')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
              </svg>
              Manual Entry
            </button>
          </div>
          <button
            className="jake-order-modal-close"
            onClick={handleClose}
            disabled={isStreaming || formLoading}
            title="Close"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {mode === 'ai' ? (
          <>
            {/* AI Chat Messages */}
            <div className="jake-order-modal-messages">
              {messages.length === 0 ? (
                <div className="jake-order-modal-welcome">
                  <p>Tell me about the Spikes Gear order you need to create.</p>
                  <p className="jake-order-modal-hint">
                    Include the customer's name and phone, and each shirt's name-on-back, size, and quantity.
                  </p>
                  <div className="jake-order-modal-examples">
                    <button onClick={() => setInput('Spikes Gear order for ')}>
                      Start with customer...
                    </button>
                    <button onClick={() => setInput('I need shirts for a whole team: ')}>
                      Multiple shirts...
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {messages.map(message => (
                    <div key={message.id} className={`jake-order-message jake-order-message-${message.role}`}>
                      {message.role === 'assistant' && (
                        <div className="jake-order-message-avatar">J</div>
                      )}
                      <div className="jake-order-message-content">
                        {message.role === 'assistant' ? (
                          <JakeMessage content={message.content} />
                        ) : (
                          <div className="jake-order-message-text">{message.content}</div>
                        )}
                      </div>
                    </div>
                  ))}
                  {isStreaming && messages[messages.length - 1]?.content === '' && (
                    <div className="jake-order-message jake-order-message-assistant">
                      <div className="jake-order-message-avatar">J</div>
                      <div className="jake-order-message-content">
                        <div className="jake-order-typing">
                          <span></span><span></span><span></span>
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {/* AI Chat Input */}
            <div className="jake-order-modal-input">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={ordersCreated ? "Order created! Closing..." : "Describe the Spikes Gear order..."}
                rows={2}
                disabled={isStreaming || ordersCreated}
              />
              <button
                className="jake-order-send-btn"
                onClick={sendMessage}
                disabled={!input.trim() || isStreaming || ordersCreated}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
                </svg>
              </button>
            </div>
          </>
        ) : (
          /* Manual Form */
          <form className="jake-order-manual-form" onSubmit={handleFormSubmit}>
            {formError && <div className="jake-order-form-error">{formError}</div>}

            <div className="jake-order-form-grid">
              <div className="jake-order-form-group">
                <label>Customer Name *</label>
                <input
                  type="text"
                  value={formData.customer_name}
                  onChange={(e) => handleFormChange('customer_name', e.target.value)}
                  required
                />
              </div>

              <div className="jake-order-form-group">
                <label>Phone *</label>
                <input
                  type="text"
                  value={formData.customer_phone}
                  onChange={(e) => handleFormChange('customer_phone', e.target.value)}
                  placeholder="830-555-1234"
                  required
                />
              </div>

              <div className="jake-order-form-group">
                <label>Price per Shirt</label>
                <div className="jake-order-fixed-price">
                  ${PRICE_PER_SHIRT.toFixed(2)} fixed (${BASE_SHIRT_PRICE.toFixed(2)} shirt + ${NAME_PRINT_FEE.toFixed(2)} name print)
                </div>
              </div>

              <div className="jake-order-form-group">
                <label>Salesperson</label>
                <select
                  value={formData.salesperson_id}
                  onChange={(e) => handleFormChange('salesperson_id', e.target.value)}
                >
                  <option value="">Select...</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>
                      {u.display_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="jake-order-form-group jake-order-form-full">
                <label>Shirts *</label>
                <div className="jake-order-items-table">
                  {formData.items.map((item, index) => (
                    <div className="jake-order-items-row" key={index}>
                      <input
                        type="text"
                        placeholder="Name to print on back"
                        value={item.name_on_back}
                        onChange={(e) => handleItemChange(index, 'name_on_back', e.target.value)}
                        maxLength="100"
                      />
                      <select
                        value={item.size}
                        onChange={(e) => handleItemChange(index, 'size', e.target.value)}
                      >
                        <option value="">Size</option>
                        {SIZES.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                      <input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                      />
                      <button
                        type="button"
                        className="jake-order-remove-item"
                        onClick={() => removeItem(index)}
                        disabled={formData.items.length === 1}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
                <button type="button" className="jake-order-add-item" onClick={addItem}>
                  + Add Another Shirt
                </button>
              </div>

              <div className="jake-order-form-group jake-order-form-full">
                <label>Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => handleFormChange('notes', e.target.value)}
                  rows={2}
                />
              </div>
            </div>

            <div className="jake-order-form-actions">
              <button type="button" onClick={handleClose} disabled={formLoading}>
                Cancel
              </button>
              <button type="submit" className="jake-order-form-submit" disabled={formLoading}>
                {formLoading ? 'Creating...' : 'Create Order'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default JakeOrderModal;
